import { useState, useEffect, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import PageHeader from '@/components/Layout/PageHeader';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { formatCurrency, formatDate } from '@/lib/utils';
import { toast } from 'sonner';
import {
  MagnifyingGlass, DownloadSimple, ArrowsClockwise, User, Ear, Stethoscope, Buildings, Package,
  CaretUp, CaretDown, X,
} from '@phosphor-icons/react';
import CustomerDrillDownDialog from '@/components/CustomerDrillDownDialog';

const SCOPES = [
  { key: 'customers', label: 'Customers', icon: User, listKey: 'customers' },
  { key: 'audiologists', label: 'Audiologists', icon: Stethoscope, listKey: 'audiologists' },
  { key: 'clinics', label: 'Clinics', icon: Buildings, listKey: 'clinics' },
  { key: 'items', label: 'Items', icon: Package, listKey: 'items' },
];

const SEGMENTS = [
  { value: 'hearing_aid_buyers', label: 'Hearing Aid Buyers', icon: Ear },
  { value: 'diagnostics_only', label: 'Diagnostics Only', icon: Stethoscope },
  { value: 'all_purchasing', label: 'All Purchasing', icon: User },
];

// Column definitions per scope
const COLUMNS = {
  customers: [
    { key: 'name', label: 'Name', filter: 'text' },
    { key: 'mobile', label: 'Mobile', filter: 'text' },
    { key: 'city', label: 'City', filter: 'text' },
    { key: 'state', label: 'State', filter: 'text' },
    { key: 'devices', label: 'Devices', filter: 'text', filterKey: 'device' },
    { key: 'clinics', label: 'Clinic', filter: 'text', filterKey: 'clinic' },
    { key: 'audiologists', label: 'Audiologist', filter: 'text', filterKey: 'audiologist' },
    { key: 'lead_sources', label: 'Lead Source', filter: 'text', filterKey: 'lead_source' },
    { key: 'money_paid', label: 'Money Paid', align: 'right', type: 'currency', filter: 'currency-range' },
    { key: 'purchase_count', label: 'Invoices', align: 'right' },
    { key: 'last_purchase_date', label: 'Last Purchase' },
  ],
  audiologists: [
    { key: 'name', label: 'Audiologist', filter: 'text' },
    { key: 'revenue', label: 'Revenue', align: 'right', type: 'currency' },
    { key: 'ha_revenue', label: 'HA Revenue', align: 'right', type: 'currency' },
    { key: 'invoice_count', label: 'Invoices', align: 'right' },
    { key: 'customer_count', label: 'Customers', align: 'right' },
    { key: 'clinics', label: 'Clinics', filter: 'text', filterKey: 'clinic' },
    { key: 'last_invoice_date', label: 'Last Invoice' },
  ],
  clinics: [
    { key: 'name', label: 'Clinic', filter: 'text' },
    { key: 'revenue', label: 'Revenue', align: 'right', type: 'currency' },
    { key: 'ha_revenue', label: 'HA Revenue', align: 'right', type: 'currency' },
    { key: 'invoice_count', label: 'Invoices', align: 'right' },
    { key: 'customer_count', label: 'Customers', align: 'right' },
    { key: 'audiologist_count', label: 'Audiologists', align: 'right' },
    { key: 'last_invoice_date', label: 'Last Invoice' },
  ],
  items: [
    { key: 'name', label: 'Item', filter: 'text' },
    { key: 'category', label: 'Category', filter: 'text' },
    { key: 'hsn', label: 'HSN' },
    { key: 'qty', label: 'Qty', align: 'right' },
    { key: 'revenue', label: 'Revenue', align: 'right', type: 'currency' },
    { key: 'invoice_count', label: 'Invoices', align: 'right' },
    { key: 'customer_count', label: 'Customers', align: 'right' },
    { key: 'clinic_count', label: 'Clinics', align: 'right' },
    { key: 'last_sold_date', label: 'Last Sold' },
  ],
};

function Pagination({ page, pageSize, total, setPage, setPageSize, idPrefix }) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 py-2" data-testid={`${idPrefix}-pagination`}>
      <div className="flex items-center gap-2 text-xs text-[#64748B]">
        <span>Show</span>
        <select value={pageSize} onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }} className="h-8 rounded border border-[#E2E8F0] px-2" data-testid={`${idPrefix}-page-size`}>
          {[25, 50, 100, 200, 500].map(s => <option key={s} value={s}>{s}</option>)}
        </select>
        <span>per page · {Math.min((page - 1) * pageSize + 1, total).toLocaleString('en-IN')}–{Math.min(page * pageSize, total).toLocaleString('en-IN')} of {total.toLocaleString('en-IN')}</span>
      </div>
      <div className="flex items-center gap-1">
        <Button variant="outline" size="sm" className="h-8" disabled={page === 1} onClick={() => setPage(1)}>«</Button>
        <Button variant="outline" size="sm" className="h-8" disabled={page === 1} onClick={() => setPage(p => Math.max(1, p - 1))}>Prev</Button>
        <span className="text-xs text-[#64748B] px-2">Page {page} of {totalPages}</span>
        <Button variant="outline" size="sm" className="h-8" disabled={page * pageSize >= total} onClick={() => setPage(p => p + 1)}>Next</Button>
        <Button variant="outline" size="sm" className="h-8" disabled={page * pageSize >= total} onClick={() => setPage(totalPages)}>»</Button>
      </div>
    </div>
  );
}

function CellValue({ row, col }) {
  const v = row[col.key];
  if (col.type === 'currency') return <span className="font-medium text-emerald-700">{formatCurrency(v || 0)}</span>;
  if (col.key.endsWith('_date')) return <span className="text-xs text-[#64748B]">{formatDate(v)}</span>;
  if (Array.isArray(v)) {
    if (v.length === 0) return <span className="text-[#64748B]">-</span>;
    return (
      <div className="flex flex-wrap gap-1 max-w-[260px]">
        {v.slice(0, 2).map((d, i) => <span key={i} className="bg-[#F8FAFC] px-1.5 py-0.5 rounded text-[10px]">{d}</span>)}
        {v.length > 2 && <span className="text-[10px] text-[#64748B]">+{v.length - 2}</span>}
      </div>
    );
  }
  return <span>{v ?? '-'}</span>;
}

export default function CustomerReportsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [scope, setScope] = useState(searchParams.get('scope') || 'customers');
  const [segment, setSegment] = useState(searchParams.get('segment') || 'hearing_aid_buyers');
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalRevenue, setTotalRevenue] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortDir, setSortDir] = useState('desc');
  const [columnFilters, setColumnFilters] = useState({});
  const [minPaid, setMinPaid] = useState('');
  const [maxPaid, setMaxPaid] = useState('');
  const [drillRow, setDrillRow] = useState(null);
  const [drillDetail, setDrillDetail] = useState(null);
  const [drillLoading, setDrillLoading] = useState(false);
  const [custDrill, setCustDrill] = useState(null);
  const [revSummary, setRevSummary] = useState(null);

  const cols = COLUMNS[scope] || [];
  const defaultSort = useMemo(() => ({
    customers: 'last_purchase_date',
    audiologists: 'revenue',
    clinics: 'revenue',
    items: 'revenue',
  })[scope], [scope]);
  useEffect(() => { setSortBy(defaultSort); setSortDir('desc'); setPage(1); setColumnFilters({}); }, [scope, defaultSort]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = {
        page, page_size: pageSize, sort_by: sortBy || defaultSort, sort_dir: sortDir,
        start_date: startDate, end_date: endDate,
        ...columnFilters,
      };
      if (scope === 'customers') {
        params.segment = segment;
        if (minPaid) params.min_paid = Number(minPaid);
        if (maxPaid) params.max_paid = Number(maxPaid);
      }
      const { data } = await api.get(`/reports/${scope}`, { params });
      const listKey = SCOPES.find(s => s.key === scope).listKey;
      setRows(data[listKey] || []);
      setTotal(data.total || 0);
      setTotalRevenue(data.total_revenue || 0);
    } catch (e) {
      toast.error('Failed to load report');
    } finally { setLoading(false); }
  }, [scope, segment, page, pageSize, sortBy, sortDir, startDate, endDate, columnFilters, minPaid, maxPaid, defaultSort]);
  useEffect(() => { load(); }, [load]);

  // Dashboard-identical revenue breakdown (Total / Net / Inter-branch) for the date range
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get('/reports/summary', { params: { start_date: startDate, end_date: endDate } });
        setRevSummary(data);
      } catch { /* non-blocking */ }
    })();
  }, [startDate, endDate]);

  const exportCsv = async () => {
    if (scope !== 'customers') { toast.message('CSV export available for Customers scope'); return; }
    try {
      const resp = await api.get(`/reports/customers/export`, {
        params: { segment, start_date: startDate, end_date: endDate }, responseType: 'blob',
      });
      const url = window.URL.createObjectURL(resp.data);
      const a = document.createElement('a');
      a.href = url; a.download = `${scope}_${segment}_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      toast.success('CSV downloaded');
    } catch (e) { toast.error('Export failed'); }
  };

  const openDrill = async (row) => {
    if (scope === 'customers') {
      setCustDrill({ id: row.customer_id, name: row.name });
      return;
    }
    setDrillRow(row); setDrillDetail(null); setDrillLoading(true);
    try {
      const endpoint = {
        customers: `/reports/customer-detail/${row.customer_id}`,
        audiologists: `/reports/audiologist-detail/${encodeURIComponent(row.name)}`,
        clinics: `/reports/clinic-detail/${encodeURIComponent(row.name)}`,
        items: `/reports/item-detail`,
      }[scope];
      const cfg = scope === 'items' ? { params: { name: row.name, start_date: startDate, end_date: endDate } } : { params: { start_date: startDate, end_date: endDate } };
      const { data } = await api.get(endpoint, cfg);
      setDrillDetail(data);
    } catch (e) { toast.error('Failed to load detail'); }
    setDrillLoading(false);
  };

  const toggleSort = (key) => {
    if (sortBy === key) setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    else { setSortBy(key); setSortDir('desc'); }
    setPage(1);
  };

  const updateFilter = (key, value) => {
    setColumnFilters(prev => {
      const next = { ...prev };
      if (value) next[key] = value; else delete next[key];
      return next;
    });
    setPage(1);
  };

  const clearAll = () => {
    setColumnFilters({}); setMinPaid(''); setMaxPaid(''); setPage(1);
  };

  const activeFilterCount = Object.keys(columnFilters).length + (minPaid ? 1 : 0) + (maxPaid ? 1 : 0);

  return (
    <div className="p-6 max-w-[1700px] mx-auto" data-testid="customer-reports-page">
      <PageHeader title="Enterprise Reports" subtitle="Cohort analysis, drill-downs and exports across customers, audiologists, clinics & items" />

      {/* Scope Tabs */}
      <Tabs value={scope} onValueChange={(v) => { setScope(v); setSearchParams({ scope: v }); }} className="mt-6">
        <TabsList className="bg-[#F8FAFC]">
          {SCOPES.map(s => (
            <TabsTrigger key={s.key} value={s.key} data-testid={`scope-${s.key}`} className="data-[state=active]:bg-white">
              <s.icon size={14} className="mr-1.5" /> {s.label}
            </TabsTrigger>
          ))}
        </TabsList>
      </Tabs>

      {/* Segment selector (customers only) */}
      {scope === 'customers' && (
        <div className="mt-4 flex flex-wrap gap-2" data-testid="customer-segments">
          {SEGMENTS.map(s => (
            <button key={s.value} onClick={() => { setSegment(s.value); setPage(1); }}
              className={`px-3 py-1.5 rounded-lg border text-xs font-medium ${segment === s.value ? 'bg-[#0F4C81] text-white border-[#0F4C81]' : 'bg-white text-[#64748B] border-[#E2E8F0] hover:border-[#0F4C81]'}`}
              data-testid={`segment-${s.value}`}>
              <s.icon size={12} className="inline mr-1" /> {s.label}
            </button>
          ))}
        </div>
      )}

      {/* Summary cards + global filters */}
      <div className="mt-4 grid grid-cols-1 md:grid-cols-4 gap-3">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wide text-[#64748B]">Total {SCOPES.find(s => s.key === scope).label}</p>
          <p className="text-xl font-bold text-[#0F4C81]" data-testid="report-total-count">{total.toLocaleString('en-IN')}</p>
        </div>
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-3">
          <p className="text-[10px] uppercase tracking-wide text-[#64748B]">Revenue</p>
          <p className="text-xl font-bold text-emerald-700">{formatCurrency(totalRevenue)}</p>
        </div>
        <div className="md:col-span-2 bg-white border border-[#E2E8F0] rounded-lg p-3 flex flex-wrap items-center gap-3">
          <label className="text-[10px] uppercase tracking-wide text-[#64748B]">From</label>
          <Input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setPage(1); }} className="w-36 h-9" data-testid="report-from-date" />
          <label className="text-[10px] uppercase tracking-wide text-[#64748B]">To</label>
          <Input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setPage(1); }} className="w-36 h-9" data-testid="report-to-date" />
          {activeFilterCount > 0 && <button onClick={clearAll} className="text-xs text-red-600 hover:underline" data-testid="clear-all-filters"><X size={10} className="inline" /> Clear all ({activeFilterCount})</button>}
          <div className="ml-auto flex gap-2">
            <Button variant="outline" size="sm" onClick={load} data-testid="refresh-btn"><ArrowsClockwise size={14} className="mr-1" /> Refresh</Button>
            <Button size="sm" className="bg-[#0F4C81] text-white hover:bg-[#0B3C68]" onClick={exportCsv} data-testid="export-btn"><DownloadSimple size={14} className="mr-1" /> Export CSV</Button>
          </div>
        </div>
      </div>

      {/* Revenue Breakdown — computed exactly like the Dashboard (Total + SO − CN = Net) */}
      {revSummary && (
        <div className="mt-3 bg-white border border-[#E2E8F0] rounded-lg p-5" data-testid="report-revenue-breakdown">
          <h3 className="text-sm font-semibold text-[#0F172A] mb-4">Revenue Breakdown {(startDate || endDate) ? '' : '(all time)'}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div>
              <p className="text-[10px] uppercase tracking-wide text-[#64748B] mb-1">Total Revenue</p>
              <p className="text-lg font-bold text-[#0F4C81]" data-testid="report-breakdown-total">{formatCurrency(revSummary.total_revenue)}</p>
              <p className="text-[10px] text-[#94A3B8] mt-0.5">{revSummary.invoice_count || 0} invoices · excl. inter-branch</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-[#64748B] mb-1">+ Sales Orders</p>
              <p className="text-lg font-bold text-emerald-700">{formatCurrency(revSummary.sales_orders_total)}</p>
            </div>
            <div>
              <p className="text-[10px] uppercase tracking-wide text-[#64748B] mb-1">− Credit Notes</p>
              <p className="text-lg font-bold text-red-600">{formatCurrency(revSummary.credit_notes_total)}</p>
            </div>
            <div className="bg-[#0F4C81] rounded-lg px-3 py-2">
              <p className="text-[10px] uppercase tracking-wide text-emerald-300 mb-1">= Net Revenue</p>
              <p className="text-lg font-bold text-white" data-testid="report-breakdown-net">{formatCurrency(revSummary.net_revenue)}</p>
            </div>
          </div>
          <div className="mt-3 pt-3 border-t border-[#E2E8F0] flex items-center justify-between" data-testid="report-inter-branch-line">
            <span className="text-[11px] text-[#64748B]">Inter-branch Transfers <span className="text-[#94A3B8]">(internal — excluded from revenue)</span> · {revSummary.inter_branch_count || 0} entries</span>
            <span className="text-sm font-semibold text-[#94A3B8]" data-testid="report-inter-branch-total">{formatCurrency(revSummary.inter_branch_transfers)}</span>
          </div>
        </div>
      )}
      {scope === 'customers' && (
        <div className="mt-3 flex flex-wrap items-center gap-2">
          <span className="text-[10px] uppercase tracking-wide text-[#64748B]">Money paid:</span>
          <Input type="number" placeholder="Min ₹" value={minPaid} onChange={e => { setMinPaid(e.target.value); setPage(1); }} className="w-32 h-8" data-testid="min-paid" />
          <Input type="number" placeholder="Max ₹" value={maxPaid} onChange={e => { setMaxPaid(e.target.value); setPage(1); }} className="w-32 h-8" data-testid="max-paid" />
        </div>
      )}

      {/* Top pagination */}
      {total > 0 && (
        <div className="mt-4 bg-white border border-[#E2E8F0] rounded-lg px-3">
          <Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} idPrefix="top" />
        </div>
      )}

      {/* Table */}
      <div className="mt-3 bg-white border border-[#E2E8F0] rounded-lg overflow-x-auto" data-testid="report-table">
        <table className="w-full text-sm">
          <thead className="bg-[#F8FAFC] text-[#64748B] text-xs">
            <tr>
              {cols.map(c => (
                <th key={c.key} className={`p-2 font-medium select-none ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                  <button onClick={() => toggleSort(c.key)} className="inline-flex items-center gap-1 hover:text-[#0F4C81]" data-testid={`sort-${c.key}`}>
                    {c.label}
                    {sortBy === c.key ? (sortDir === 'asc' ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />) : <CaretDown size={10} className="opacity-30" />}
                  </button>
                </th>
              ))}
            </tr>
            <tr className="border-t border-[#E2E8F0] bg-white">
              {cols.map(c => (
                <th key={c.key} className="p-1.5">
                  {c.filter === 'text' && (
                    <Input
                      value={columnFilters[c.filterKey || c.key] || ''}
                      onChange={e => updateFilter(c.filterKey || c.key, e.target.value)}
                      placeholder="Filter…"
                      className="h-7 text-xs"
                      data-testid={`filter-${c.key}`}
                    />
                  )}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={cols.length} className="text-center py-8 text-[#64748B]">Loading...</td></tr>
            ) : rows.length === 0 ? (
              <tr><td colSpan={cols.length} className="text-center py-8 text-[#64748B]">No data found</td></tr>
            ) : rows.map((r, i) => (
              <tr key={r.customer_id || r.item_id || r.name || i} className="border-t border-[#E2E8F0] hover:bg-[#F8FAFC] cursor-pointer" onClick={() => openDrill(r)} data-testid={`row-${scope}-${i}`}>
                {cols.map(c => (
                  <td key={c.key} className={`p-2 ${c.align === 'right' ? 'text-right' : 'text-left'}`}>
                    <CellValue row={r} col={c} />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Bottom pagination */}
      {total > 0 && (
        <div className="mt-2 bg-white border border-[#E2E8F0] rounded-lg px-3">
          <Pagination page={page} pageSize={pageSize} total={total} setPage={setPage} setPageSize={setPageSize} idPrefix="bottom" />
        </div>
      )}

      {/* Drill-down dialog */}
      <DrillDownDialog scope={scope} row={drillRow} detail={drillDetail} loading={drillLoading} onClose={() => { setDrillRow(null); setDrillDetail(null); }} />
      <CustomerDrillDownDialog open={!!custDrill} onOpenChange={o => { if (!o) setCustDrill(null); }} customerId={custDrill?.id || ''} customerName={custDrill?.name || ''} />
    </div>
  );
}


function DrillDownDialog({ scope, row, detail, loading, onClose }) {
  if (!row) return null;
  const title = scope === 'customers' ? row.name : row.name;
  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" data-testid="drill-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Plus_Jakarta_Sans'] flex items-center gap-2">
            {scope === 'customers' && <User size={18} />}
            {scope === 'audiologists' && <Stethoscope size={18} />}
            {scope === 'clinics' && <Buildings size={18} />}
            {scope === 'items' && <Package size={18} />}
            {title}
          </DialogTitle>
        </DialogHeader>
        {loading ? <div className="py-12 text-center text-[#64748B]">Loading details...</div>
          : !detail ? <div className="py-12 text-center text-[#64748B]">No data</div>
          : scope === 'customers' ? <CustomerDetail detail={detail} />
          : scope === 'audiologists' ? <AudiologistDetail detail={detail} />
          : scope === 'clinics' ? <ClinicDetail detail={detail} />
          : <ItemDetail detail={detail} />}
      </DialogContent>
    </Dialog>
  );
}

function StatCard({ label, value, accent = 'emerald' }) {
  const cls = { emerald: 'text-emerald-700', amber: 'text-amber-700', red: 'text-red-600', blue: 'text-blue-700' }[accent] || 'text-[#0F4C81]';
  return (
    <div className="bg-[#F8FAFC] rounded-lg p-3">
      <p className="text-[10px] uppercase tracking-wide text-[#64748B]">{label}</p>
      <p className={`text-lg font-bold ${cls}`}>{value}</p>
    </div>
  );
}

function MiniTable({ title, headers, rows, render, testid }) {
  return (
    <div className="border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid={testid}>
      <div className="bg-[#F8FAFC] px-3 py-2 border-b border-[#E2E8F0]">
        <h3 className="text-sm font-medium">{title}</h3>
      </div>
      <div className="max-h-72 overflow-y-auto">
        <table className="w-full text-xs">
          <thead className="bg-white text-[#64748B] sticky top-0">
            <tr>{headers.map((h, i) => <th key={i} className={`p-2 ${h.right ? 'text-right' : 'text-left'} font-medium`}>{h.label}</th>)}</tr>
          </thead>
          <tbody>{rows.length === 0 ? <tr><td colSpan={headers.length} className="p-4 text-center text-[#64748B]">No data</td></tr> : rows.map(render)}</tbody>
        </table>
      </div>
    </div>
  );
}

function CustomerDetail({ detail }) {
  const { customer, summary, devices, invoices, payments, serials } = detail;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Total Billed" value={formatCurrency(summary.total_billed)} accent="blue" />
        <StatCard label="Paid" value={formatCurrency(summary.total_paid)} />
        <StatCard label="Balance" value={formatCurrency(summary.total_balance)} accent={summary.total_balance > 0 ? 'red' : 'emerald'} />
        <StatCard label="Lifetime Value" value={formatCurrency(summary.lifetime_value)} />
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <StatCard label="Invoices" value={summary.invoice_count} accent="blue" />
        <StatCard label="HA Devices" value={summary.ha_devices_count} />
        <StatCard label="Diagnostics" value={summary.diagnostics_count} accent="amber" />
        <StatCard label="Serials" value={(serials || []).length} />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
        <div className="border border-[#E2E8F0] rounded-lg p-3">
          <p className="text-[10px] uppercase text-[#64748B] mb-1">Contact</p>
          <p className="font-medium">{customer.display_name || customer.name}</p>
          <p className="text-xs text-[#64748B]">{customer.mobile || customer.phone || '-'} · {customer.email || '-'}</p>
          <p className="text-xs text-[#64748B] mt-1">{customer.billing_address || '-'}</p>
          <p className="text-xs text-[#64748B]">{customer.billing_city || '-'}, {customer.billing_state || '-'}</p>
        </div>
        <div className="border border-[#E2E8F0] rounded-lg p-3">
          <p className="text-[10px] uppercase text-[#64748B] mb-1">GST / Tax</p>
          <p className="text-xs">{customer.gst_no || 'Not registered'}</p>
        </div>
      </div>
      <MiniTable title={`Devices/Services (${devices.length})`} headers={[{ label: 'Item' }, { label: 'Category' }, { label: 'Qty', right: true }, { label: 'Amount', right: true }, { label: 'Last' }]}
        rows={devices} render={(d, i) => (
          <tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2 font-medium">{d.name}</td><td className="p-2 text-[#64748B]">{d.category}</td><td className="p-2 text-right">{d.qty}</td><td className="p-2 text-right font-medium text-emerald-700">{formatCurrency(d.amount)}</td><td className="p-2 text-[#64748B]">{formatDate(d.last_date)}</td></tr>
        )} testid="cust-devices" />
      <MiniTable title={`Invoices (${invoices.length})`} headers={[{ label: 'Invoice #' }, { label: 'Date' }, { label: 'Clinic' }, { label: 'Audiologist' }, { label: 'Amount', right: true }, { label: 'Status' }]}
        rows={invoices.slice(0, 30)} render={(inv, i) => (
          <tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2 font-mono text-[#0F4C81]">{inv.invoice_number}</td><td className="p-2 text-[#64748B]">{inv.date}</td><td className="p-2 text-xs">{inv.clinic_name || inv.location_name || '-'}</td><td className="p-2 text-xs">{inv.audiologist_name || '-'}</td><td className="p-2 text-right font-medium">{formatCurrency(inv.grand_total)}</td><td className="p-2"><span className={`text-[10px] px-1.5 py-0.5 rounded ${inv.status === 'paid' ? 'bg-emerald-50 text-emerald-700' : inv.status === 'partially_paid' ? 'bg-amber-50 text-amber-700' : 'bg-gray-100 text-gray-600'}`}>{inv.status}</span></td></tr>
        )} testid="cust-invoices" />
      <MiniTable title={`Payments (${payments.length})`} headers={[{ label: 'Payment #' }, { label: 'Date' }, { label: 'Mode' }, { label: 'Amount', right: true }, { label: 'Account' }]}
        rows={payments.slice(0, 30)} render={(p, i) => (
          <tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2 font-medium">{p.payment_number}</td><td className="p-2 text-[#64748B]">{formatDate(p.payment_date)}</td><td className="p-2 capitalize">{(p.payment_mode || '').replace(/_/g, ' ')}</td><td className="p-2 text-right font-medium text-emerald-700">{formatCurrency(p.amount)}</td><td className="p-2 text-xs text-[#64748B]">{p.account_name || '-'}</td></tr>
        )} testid="cust-payments" />
      {serials && serials.length > 0 && (
        <MiniTable title={`Serials Purchased (${serials.length})`} headers={[{ label: 'Serial' }, { label: 'Item' }, { label: 'Invoice' }, { label: 'Date' }]}
          rows={serials.slice(0, 50)} render={(s, i) => (
            <tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2 font-mono text-emerald-700">{s.serial}</td><td className="p-2 text-xs">{s.item}</td><td className="p-2 font-mono text-xs">{s.invoice}</td><td className="p-2 text-[#64748B] text-xs">{s.date}</td></tr>
          )} testid="cust-serials" />
      )}
    </div>
  );
}

function AudiologistDetail({ detail }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Revenue" value={formatCurrency(detail.summary.total_revenue)} />
        <StatCard label="Invoices" value={detail.summary.invoice_count} accent="blue" />
        <StatCard label="Customers" value={detail.summary.customer_count} />
        <StatCard label="Clinics" value={detail.summary.clinic_count} accent="amber" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <MiniTable title={`Top Customers (${detail.top_customers.length})`} headers={[{ label: 'Customer' }, { label: 'Invoices', right: true }, { label: 'Revenue', right: true }]}
          rows={detail.top_customers} render={(c, i) => (
            <tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2">{c.name}</td><td className="p-2 text-right">{c.count}</td><td className="p-2 text-right text-emerald-700 font-medium">{formatCurrency(c.revenue)}</td></tr>
          )} testid="aud-customers" />
        <MiniTable title={`Top Items (${detail.top_items.length})`} headers={[{ label: 'Item' }, { label: 'Category' }, { label: 'Qty', right: true }, { label: 'Revenue', right: true }]}
          rows={detail.top_items} render={(it, i) => (
            <tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2 text-xs">{it.name}</td><td className="p-2 text-[#64748B]">{it.category}</td><td className="p-2 text-right">{it.qty}</td><td className="p-2 text-right text-emerald-700">{formatCurrency(it.revenue)}</td></tr>
          )} testid="aud-items" />
      </div>
      <MiniTable title={`Clinics`} headers={[{ label: 'Clinic' }, { label: 'Invoices', right: true }, { label: 'Revenue', right: true }]}
        rows={detail.clinics} render={(c, i) => (<tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2">{c.name}</td><td className="p-2 text-right">{c.count}</td><td className="p-2 text-right text-emerald-700 font-medium">{formatCurrency(c.revenue)}</td></tr>)} testid="aud-clinics" />
    </div>
  );
}

function ClinicDetail({ detail }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Revenue" value={formatCurrency(detail.summary.total_revenue)} />
        <StatCard label="Invoices" value={detail.summary.invoice_count} accent="blue" />
        <StatCard label="Customers" value={detail.summary.customer_count} />
        <StatCard label="Audiologists" value={detail.summary.audiologist_count} accent="amber" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <MiniTable title="Audiologists" headers={[{ label: 'Audiologist' }, { label: 'Invoices', right: true }, { label: 'Revenue', right: true }]}
          rows={detail.top_audiologists} render={(a, i) => (<tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2">{a.name}</td><td className="p-2 text-right">{a.count}</td><td className="p-2 text-right text-emerald-700 font-medium">{formatCurrency(a.revenue)}</td></tr>)} testid="clinic-aud" />
        <MiniTable title="Lead Sources" headers={[{ label: 'Source' }, { label: 'Invoices', right: true }, { label: 'Revenue', right: true }]}
          rows={detail.lead_sources} render={(l, i) => (<tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2">{l.name}</td><td className="p-2 text-right">{l.count}</td><td className="p-2 text-right text-emerald-700 font-medium">{formatCurrency(l.revenue)}</td></tr>)} testid="clinic-ls" />
      </div>
      <MiniTable title="Top Items" headers={[{ label: 'Item' }, { label: 'Category' }, { label: 'Qty', right: true }, { label: 'Revenue', right: true }]}
        rows={detail.top_items} render={(it, i) => (<tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2 text-xs">{it.name}</td><td className="p-2 text-[#64748B]">{it.category}</td><td className="p-2 text-right">{it.qty}</td><td className="p-2 text-right text-emerald-700 font-medium">{formatCurrency(it.revenue)}</td></tr>)} testid="clinic-items" />
      <MiniTable title="Top Customers" headers={[{ label: 'Customer' }, { label: 'Invoices', right: true }, { label: 'Revenue', right: true }]}
        rows={detail.top_customers} render={(c, i) => (<tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2">{c.name}</td><td className="p-2 text-right">{c.count}</td><td className="p-2 text-right text-emerald-700 font-medium">{formatCurrency(c.revenue)}</td></tr>)} testid="clinic-cust" />
    </div>
  );
}

function ItemDetail({ detail }) {
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-4 gap-3">
        <StatCard label="Revenue" value={formatCurrency(detail.summary.total_revenue)} />
        <StatCard label="Qty Sold" value={detail.summary.total_qty} accent="blue" />
        <StatCard label="Customers" value={detail.summary.customer_count} />
        <StatCard label="Clinics" value={detail.summary.clinic_count} accent="amber" />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <MiniTable title="Top Customers" headers={[{ label: 'Customer' }, { label: 'Qty', right: true }, { label: 'Revenue', right: true }, { label: 'Last' }]}
          rows={detail.top_customers} render={(c, i) => (<tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2">{c.name}</td><td className="p-2 text-right">{c.qty}</td><td className="p-2 text-right text-emerald-700 font-medium">{formatCurrency(c.revenue)}</td><td className="p-2 text-[#64748B]">{c.last}</td></tr>)} testid="item-cust" />
        <MiniTable title="Clinics" headers={[{ label: 'Clinic' }, { label: 'Qty', right: true }, { label: 'Revenue', right: true }]}
          rows={detail.clinics} render={(c, i) => (<tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2">{c.name}</td><td className="p-2 text-right">{c.qty}</td><td className="p-2 text-right text-emerald-700 font-medium">{formatCurrency(c.revenue)}</td></tr>)} testid="item-clinic" />
      </div>
      <MiniTable title="Audiologists" headers={[{ label: 'Audiologist' }, { label: 'Qty', right: true }, { label: 'Revenue', right: true }]}
        rows={detail.audiologists} render={(a, i) => (<tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2">{a.name}</td><td className="p-2 text-right">{a.qty}</td><td className="p-2 text-right text-emerald-700 font-medium">{formatCurrency(a.revenue)}</td></tr>)} testid="item-aud" />
      {detail.serials && detail.serials.length > 0 && (
        <MiniTable title={`Serials Sold (${detail.serials.length})`} headers={[{ label: 'Serial' }, { label: 'Customer' }, { label: 'Invoice' }, { label: 'Date' }, { label: 'Clinic' }]}
          rows={detail.serials.slice(0, 100)} render={(s, i) => (<tr key={i} className="border-t border-[#E2E8F0]"><td className="p-2 font-mono text-emerald-700">{s.serial}</td><td className="p-2 text-xs">{s.customer}</td><td className="p-2 font-mono text-xs">{s.invoice}</td><td className="p-2 text-[#64748B]">{s.date}</td><td className="p-2 text-xs">{s.clinic}</td></tr>)} testid="item-serials" />
      )}
    </div>
  );
}
